/**
 * @description Component-TradeHistory
 */
import './index.less';

import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Empty from '@/components/Empty';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { ExternalLink } from '@/components/Link';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import { Tooltip } from '@/components/ToolTip';
import LiquidityRemoveTooltip from '@/components/ToolTip/LiquidityRemoveTooltip';
import UnderlineToolTip from '@/components/ToolTip/UnderlineToolTip';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useInfiniteVirtualTradeHistory } from '@/features/graph/hooks';
import { TradeHistory as TradeHistoryType, VirtualTradeType } from '@/features/graph/types';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useChainId, useEtherscanLink, useUserAddr } from '@/hooks/web3/useChain';
import TradeSideSize from '@/pages/components/TradeSideSize';
import IconPay from '@/pages/portfolio/AccountHistory/assets/icon_label_p.svg?react';
import IconReceive from '@/pages/portfolio/AccountHistory/assets/icon_label_r.svg?react';
import { formatDate } from '@/utils/timeUtils';
import { mappingToken } from '@/utils/token';
import { Side } from '@synfutures/sdks-perp';
import MobileHistoryCard from '../MobileHistoryCard';
import MobileHistorySkeleton from '../MobileHistoryCard/Skeleton';

interface IPropTypes {
  isTradePage?: boolean;
}

const TradeHistory: FC<IPropTypes> = ({ isTradePage = false }) => {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { t } = useTranslation();
  const currentPair = useCurrentPairFromUrl(chainId);

  const pair = isTradePage ? currentPair : 'all';

  const {
    histories,
    queryState: { isLoading: isLoadingTrade, isFetching },
  } = useInfiniteVirtualTradeHistory({ chainId, userAddr, pair });

  const getEtherscanLink = useEtherscanLink();
  const loading = isLoadingTrade || isFetching;

  const renderCard = (record: TradeHistoryType) => {
    const side = record.side === 'long' ? Side.LONG : Side.SHORT;

    const tradeValue = WrappedBigNumber.from(record.price || 0)
      .mul(record.size || 0)
      .abs();

    let feeType: 'paid' | 'received' | undefined = undefined;
    let fee = WrappedBigNumber.from(record?.tradeFee || 0).add(record?.protocolFee || 0);
    if (record.typeString === VirtualTradeType.MARKET) {
      feeType = 'paid';
    } else if (record.typeString === VirtualTradeType.LIMIT) {
      feeType = 'received';
    } else if (record.typeString === VirtualTradeType.RANGE) {
      fee = WrappedBigNumber.ZERO;
    } else if (record.typeString === VirtualTradeType.TAKE_OVER) {
      fee = WrappedBigNumber.ZERO;
    } else if (record.typeString === VirtualTradeType.LIQUIDATION) {
      feeType = 'paid';
    }

    return (
      <MobileHistoryCard
        key={record.id}
        header={
          chainId ? (
            <>
              <div className="value">
                <EmptyDataWrap isLoading={!record?.instrumentAddress}>
                  <TokenPairSimple
                    tradeSide={record.side ? (record.side === 'long' ? Side.LONG : Side.SHORT) : Side.FLAT}
                    // isInverse={record.isInverse}
                    baseToken={mappingToken({
                      token: record.baseToken,
                      chainId: chainId,
                    })}
                    quoteToken={mappingToken({
                      token: record.quoteToken,
                      chainId: chainId,
                    })}
                  />
                </EmptyDataWrap>
              </div>
              <div className="value">
                <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')} className="history-time-with-icon">
                  {formatDate(record.timestamp * 1000, 'MM-DD HH:mm')}
                </ExternalLink>
              </div>
            </>
          ) : undefined
        }>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.type')}</div>
          <div className="value">
            <UnderlineToolTip
              title={
                record.typeString === VirtualTradeType.RANGE ? <LiquidityRemoveTooltip record={record} /> : undefined
              }>
              <span>{record.typeString}</span>
            </UnderlineToolTip>
          </div>
        </div>

        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.size')}</div>
          <div className="value">
            <TradeSideSize
              side={side}
              size={WrappedBigNumber.from(record.size).formatNumberWithTooltip({
                isShowTBMK: true,
                suffix: record?.baseToken?.symbol,
              })}
            />
          </div>
        </div>

        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.price')}</div>
          <div className="value">{WrappedBigNumber.from(record.price).formatPriceNumberWithTooltip()}</div>
        </div>

        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.tradeValue')}</div>
          <div className="value">
            {tradeValue.formatNumberWithTooltip({
              suffix: record?.quoteToken?.symbol,
            })}
          </div>
        </div>

        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.fee')}</div>
          <div className="value">
            <EmptyDataWrap isLoading={!record?.tradeFee || fee.lte(0)}>
              <span className="syn-futures-table_card-fee">
                <Tooltip title={feeType && t(`common.table.${feeType}`)}>
                  {feeType && (
                    <span className="syn-futures-table_card-fee-icon">
                      {feeType === 'paid' ? <IconPay /> : <IconReceive />}
                    </span>
                  )}
                </Tooltip>
                {WrappedBigNumber.from(fee).formatNumberWithTooltip({
                  suffix: record?.quoteToken?.symbol,
                })}
              </span>
            </EmptyDataWrap>
          </div>
        </div>
      </MobileHistoryCard>
    );
  };

  return (
    <>
      <div className="syn-mobile-history-trade">
        {loading ? <MobileHistorySkeleton /> : histories.slice(0, 10).map(renderCard)}
      </div>
      {!loading && !!histories.length && <div className="syn-mobile-history-info">{t('mobile.history.tableInfo')}</div>}
      {!loading && !histories?.length && <Empty type="vertical" />}
    </>
  );
};

export default TradeHistory;
